import { useUser } from '@clerk/clerk-react';
import LoadingSpinner from '../common/LoadingSpinner';
import { DEV_AUTH_BYPASS } from '../../devBypass';

interface AdminRoleGuardProps {
  children: React.ReactNode;
}

export type AdminRole = 'super_admin' | 'super_admin_members' | 'partner_admin' | null;

export function useAdminRole() {
  const { user } = useUser();
  const role = (user?.publicMetadata?.role as string) || null;
  const email = user?.emailAddresses?.[0]?.emailAddress?.toLowerCase() || null;

  return {
    role: role as AdminRole,
    email,
    isSuperAdmin: role === 'super_admin',
    isSuperAdminMember: role === 'super_admin_members',
    isPartnerAdmin: role === 'partner_admin',
    canAccessBilling: role === 'super_admin', // Only full super_admin can access billing/revenue
    hasAdminAccess: role === 'super_admin' || role === 'super_admin_members' || role === 'partner_admin',
  };
}

export default function AdminRoleGuard({ children }: AdminRoleGuardProps) {
  const { isLoaded: userLoaded, user } = useUser();

  // Local dev preview: skip the platform-admin role check.
  if (DEV_AUTH_BYPASS) {
    return <>{children}</>;
  }

  if (!userLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
        <p className="ml-2">Loading user...</p>
      </div>
    );
  }

  // Debug: Log entire publicMetadata to see what's there
  console.log('[AdminRoleGuard] Full publicMetadata:', JSON.stringify(user?.publicMetadata));

  const role = (user?.publicMetadata?.role as string) || null;
  const userEmail = user?.emailAddresses?.[0]?.emailAddress || '';
  const isSuperAdmin = role === 'super_admin';
  const isSuperAdminMember = role === 'super_admin_members';
  const isPartnerAdmin = role === 'partner_admin';
  const hasAccess = isSuperAdmin || isSuperAdminMember || isPartnerAdmin;

  console.log('[AdminRoleGuard] email:', userEmail);
  console.log('[AdminRoleGuard] role from publicMetadata:', role);
  console.log('[AdminRoleGuard] hasAccess:', hasAccess);

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You do not have permission to access the admin panel.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Logged in as: {userEmail}<br />
            Role: {role || 'none'}
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
